import {
  toggleGroupConfig,
  toggleGroupConfigExtended,
} from 'components/core/ColumnPanel/utils';
import { useState } from 'react';
import { usePermissions } from 'utils/hooks';

import {
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Switch,
  Tag,
  Typography,
} from '@project44/manifest-ds';
import { Collapse } from '@project44/material-ui-deprecated';
import { useTranslation } from '@project44/translations-utils';

interface IToggleOptionGroup {
  id: string;
  isDisabled: boolean;
  isExpanded: boolean;
  toggleOptions: string[];
  onToggle: (column: string) => void;
}

function ToggleOptionGroup({
  id,
  isDisabled,
  isExpanded,
  toggleOptions,
  onToggle,
}: IToggleOptionGroup) {
  const { t } = useTranslation();
  const {
    flags: { featureExc1914 },
  } = usePermissions();
  return (
    <Flex
      css={{
        paddingLeft: '$x-large',
        marginLeft: '',
        backgroundColor: '$palette-white',
      }}
    >
      <Collapse
        aria-label={t('core.collapseSectionLabel')}
        in={isExpanded}
        collapsedSize="0px"
        style={{ padding: 0, width: '100%' }}
      >
        <Flex
          orientation="vertical"
          gap="small"
          css={{ paddingBottom: '$small' }}
        >
          {!featureExc1914
            ? toggleGroupConfig[id]?.map(({ id, label }) => (
                <Flex
                  key={label}
                  css={{ flexBasis: '48%', paddingLeft: '$large' }}
                >
                  <Checkbox
                    aria-label={t(
                      toggleOptions.includes(id)
                        ? 'core.disableColumn'
                        : 'core.enableColumn',
                      { columnName: label },
                    )}
                    isSelected={!toggleOptions.includes(id)}
                    onChange={() => {
                      onToggle(id);
                    }}
                    isDisabled={!isDisabled}
                  >
                    {label}
                  </Checkbox>
                </Flex>
              ))
            : toggleGroupConfigExtended[id]?.map(({ id, label }) => (
                <Flex
                  key={label}
                  css={{ flexBasis: '48%', paddingLeft: '$large' }}
                >
                  <Checkbox
                    aria-label={t(
                      toggleOptions.includes(id)
                        ? 'core.disableColumn'
                        : 'core.enableColumn',
                      { columnName: label },
                    )}
                    isSelected={!toggleOptions.includes(id)}
                    onChange={() => {
                      onToggle(id);
                    }}
                    isDisabled={!isDisabled}
                  >
                    {label}
                  </Checkbox>
                </Flex>
              ))}
        </Flex>
      </Collapse>
    </Flex>
  );
}

interface IColumnToggle {
  id: string;
  label: string;
  isOrder: boolean;
  isSelected: boolean;
  isToggleGroup: boolean;
  toggleOptions: string[];
  activeToggleGroup: { [key: string]: string[] };
  disableDrag: boolean;
  disableToggle: boolean;
  onToggle: (column: string) => void;
  onToggleGroup: (column: string[], state: boolean) => void;
}

const styles = {
  switch: {
    paddingRight: '$x-small',
  },
};

function ColumnToggle({
  id,
  label,
  isOrder,
  isSelected,
  isToggleGroup,
  toggleOptions,
  activeToggleGroup,
  disableDrag,
  disableToggle,
  onToggle,
  onToggleGroup,
}: IColumnToggle) {
  const { t } = useTranslation();
  const {
    flags: { featureExc1914 },
  } = usePermissions();
  const isLocked = disableDrag && disableToggle;
  const [isExpanded, setIsExpanded] = useState(true);
  const color = isLocked || !disableDrag ? '$text-secondary' : '$text-disabled';
  const groupIds = !featureExc1914
    ? toggleGroupConfig[id]?.map(({ id }) => id)
    : toggleGroupConfigExtended[id]?.map(({ id }) => id);
  const activeGroupIds = groupIds?.filter((id) => !toggleOptions.includes(id));
  const isGroupDisabled = !!activeGroupIds?.length;
  const enableToggleGroup = isToggleGroup && !isOrder;
  const tagLabel = t('core.selectedColumns', {
    count: groupIds?.length - activeToggleGroup[id]?.length,
    total: groupIds?.length,
  });

  const handleDropdown = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      <Flex
        align="center"
        justify="between"
        css={{
          padding: '$small $medium',
          backgroundColor: '$palette-white',
        }}
      >
        <Flex gap="medium" css={{ color }}>
          {isLocked ? (
            <Icon
              icon="lock"
              css={{
                fontSize: '$large',
                alignSelf: 'unset',
                paddingTop: '1px',
              }}
            />
          ) : (
            <Icon
              aria-label={t('core.dragHandleLabel', { name: label })}
              className="dragHandle"
              icon="drag_indicator"
              css={{
                cursor: disableDrag ? 'not-allowed' : 'grab',
                fontSize: '$large',
                userSelect: 'auto',
                alignSelf: 'unset',
                paddingTop: '2px',
              }}
            />
          )}
          <Flex orientation="vertical">
            <Flex gap="small">
              <Typography variant="body">{label}</Typography>
              {enableToggleGroup && (
                <Tag
                  aria-label={t('core.toggleOptionTagLabel')}
                  isDisabled={disableDrag}
                >
                  {tagLabel}
                </Tag>
              )}
            </Flex>
            {enableToggleGroup && (
              <Typography variant="caption" css={{ marginBottom: '$small' }}>
                Customize the subset columns to display in list view
              </Typography>
            )}
          </Flex>
        </Flex>
        <Flex>
          {enableToggleGroup && (
            <IconButton
              aria-label={t('core.toggleOptionGroupLabel')}
              onPress={handleDropdown}
              variant="tertiary"
            >
              {!isExpanded ? (
                <Icon icon="keyboard_arrow_down" />
              ) : (
                <Icon icon="keyboard_arrow_up" />
              )}
            </IconButton>
          )}
          {!disableToggle &&
            (enableToggleGroup ? (
              <Switch
                aria-label={t(
                  isSelected ? 'core.enableColumn' : 'core.disableColumn',
                  { columnName: label },
                )}
                isSelected={isGroupDisabled}
                onChange={() => {
                  onToggleGroup(groupIds, isGroupDisabled);
                }}
                css={styles.switch}
              />
            ) : (
              <Switch
                aria-label={t(
                  isSelected ? 'core.enableColumn' : 'core.disableColumn',
                  { columnName: label },
                )}
                isSelected={isSelected}
                onChange={() => {
                  onToggle(id);
                }}
                css={styles.switch}
              />
            ))}
        </Flex>
      </Flex>
      {enableToggleGroup && (
        <ToggleOptionGroup
          id={id}
          onToggle={onToggle}
          isExpanded={isExpanded}
          toggleOptions={toggleOptions}
          isDisabled={isGroupDisabled}
        />
      )}
    </>
  );
}

export default ColumnToggle;
